import { FormatTimeMonth } from 'features/autocheck/utils'
import { UseCaseEnumToUseCaseIdEnum } from 'features/calls/types'
import { IClaimActivities, WellKnownActivitiesEnum, WellKnownActorsEnum } from 'features/entities/types'
import styled from 'styled-components'
import { formatPhoneNumber, sendLogs } from 'utils'
import { ActorIconTypeEnum, ClaimHistoryActorIcon } from './ClaimHistoryActorIcon'

const classActor = 'actor'
const classActorActivity = 'actor-activity'
const classActorIcon = 'actor-icon'
const classActorIconContinuation = 'actor-icon-continuation'
const classPartialHeight = 'partial-height'
const classAgentTitle = 'agent-title'
const classActivityDescription = 'activity-description'
const classHistoryTimestamp = 'history-timestamp'

interface IHistoryBagProps {
  row: string
}
const TAKE_OWNERSHIP = 'take ownership'
const TOOK_OWNERSHIP = 'took ownership of this claim'
const CLAIMS_OWNERSHIP = 'claims ownership'
export const ClaimDetailsHistoryItem = ({
  claimActivites,
  index,
}: {
  claimActivites: IClaimActivities
  index: number
}): JSX.Element => {
  let actorIconType: ActorIconTypeEnum = ActorIconTypeEnum.unknown
  if (
    claimActivites.activityId === WellKnownActivitiesEnum.createFromCsv ||
    claimActivites.activityId === WellKnownActivitiesEnum.updateFromCSV
  ) {
    actorIconType = ActorIconTypeEnum.fileUpload
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.EOBFileReceived) {
    actorIconType = ActorIconTypeEnum.eobFax
  } else if (claimActivites.actorId === WellKnownActorsEnum.ivrInfoExtractor) {
    actorIconType = ActorIconTypeEnum.AIAgent
  } else if (claimActivites.actorId === WellKnownActorsEnum.rcmAgent) {
    actorIconType = ActorIconTypeEnum.humanAgent
  } else if (
    claimActivites.actorId === WellKnownActorsEnum.businessLogic ||
    claimActivites.actorId === WellKnownActorsEnum.connectorIntegration
  ) {
    actorIconType = ActorIconTypeEnum.businessLogic
  } else {
    console.log(`error: actor or activity type is unknown`)
    sendLogs({ message: `error: actor or activity type is unknown` })
  }

  // let activityDescription: string = claimActivites.activityId === WellKnownActivitiesEnum.ownership ? claimActivites.remarks : ''
  let activityDescription: string =
    (claimActivites.activityId === WellKnownActivitiesEnum.ownership &&
      claimActivites.remarks.toLocaleLowerCase() === TAKE_OWNERSHIP) ||
      (claimActivites.activityId === WellKnownActivitiesEnum.ownership &&
        claimActivites.remarks.toLocaleLowerCase() === CLAIMS_OWNERSHIP)
      ? TOOK_OWNERSHIP
      : claimActivites.remarks
  if (claimActivites.activityId === WellKnownActivitiesEnum.ownership) {
    activityDescription = claimActivites.remarks
  }
  if (claimActivites.activityId === WellKnownActivitiesEnum.valueChanged) {
    activityDescription = `edited values with comment: "${claimActivites.remarks}"`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.createFromCsv) {
    activityDescription = `uploaded this claim in ${claimActivites.remarks}`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.updateFromCSV) {
    activityDescription = `reuploaded this claim in ${claimActivites.remarks}`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.uploadFromConnector) {
    activityDescription = `uploaded this claim.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.reuploadFromConnector) {
    activityDescription = `reuploaded this claim.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.createDiagnosticClaim) {
    activityDescription = `created this diagnostic claim.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.archived) {
    activityDescription = `archived this claim.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.unarchived) {
    activityDescription = `unarchived this claim.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.EOBFileReceived) {
    activityDescription = `Explanation of Benefits was received from the payer.`
  } else if (claimActivites.activityId === WellKnownActivitiesEnum.callCreated) {
    let dialed = claimActivites.callJob?.phoneNumber
    if (dialed) {
      dialed = ' on ' + formatPhoneNumber(dialed)
    } else {
      dialed = ''
    }
    const callType =
      claimActivites.callJob?.useCase === UseCaseEnumToUseCaseIdEnum.CSI
        ? 'claim status'
        : claimActivites.callJob?.useCase === UseCaseEnumToUseCaseIdEnum.EOB
          ? 'claim status & EOB'
          : claimActivites.callJob?.useCase === UseCaseEnumToUseCaseIdEnum.ASC
            ? 'appeal status'
            : 'eligibility and benefits'
    activityDescription = `called ${claimActivites.callJob?.preferredPayerName ? claimActivites.callJob.preferredPayerName : claimActivites.remarks}${dialed} to get ` + callType
  }

  const agentTitle = !claimActivites.userName ? claimActivites.actorName : claimActivites.userName
  return (
    <Div row={index.toString()}>
      <div className={classActor}>
        <ClaimHistoryActorIcon actorIconType={actorIconType} className={classActorIcon} />
      </div>
      <div className={classActorActivity}>
        <span className={classAgentTitle}>
          {claimActivites.activityId != WellKnownActivitiesEnum.EOBFileReceived && agentTitle}{' '}
        </span>
        <span className={classActivityDescription}>{activityDescription}</span>
        <div className={classHistoryTimestamp}>{FormatTimeMonth(new Date(claimActivites.created!))}</div>
      </div>
    </Div>
  )
}

const Div = styled.div.attrs((props: IHistoryBagProps) => ({ 'data-row': props.row })) <IHistoryBagProps>`
  display: flex;
  margin-bottom: 1em;
  .${classActor} {
    grid-column: 1;
    grid-row: ${(props) => 1 + Number(props.row)};
    display: grid;
    grid-template: auto minmax(0, 1fr) / auto;
    justify-content: center;
  }
  .${classActorIcon} {
    grid-column: 1;
    grid-row: 1;
  }
  .${classActorIconContinuation} {
    position: relative;
    grid-column: 1;
    grid-row: 2;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
  }
  .${classPartialHeight} {
    height: 90%;
    width: 100%;
    object-fit: cover;
  }
  .${classActorActivity} {
    padding-left: 0.5em;
    grid-column: 2;
    grid-row: ${(props) => 1 + Number(props.row)};
  }

  .${classAgentTitle} {
    ${({ theme }) => theme.typography.s.medium};
    white-space: wrap;
    color: #576573;
  }

  .${classActivityDescription} {
    ${({ theme }) => theme.typography.s.medium};
    color: #576573;
  }

  .${classHistoryTimestamp} {
    padding-top: 0.5em;
    ${({ theme }) => theme.typography.xs.normal};
    color: #576573;
    opacity: 0.7;
  }
`
